import {Component, inject, OnInit, ViewChild} from '@angular/core';
import {Router, RouterOutlet} from '@angular/router';
import {HeaderComponent} from './components/header/header.component';
import {FooterComponent} from './components/footer/footer.component';
import {CommonModule} from '@angular/common';
import {LoaderService} from './loader.service';
import {IdleTimeoutService} from './services/idle-timeout.service';
import {PaymentBlockService} from './services/payment-block.service';
import {PacService} from './shared/services/pac.service';
import {CustomModalComponent} from './components/custom-modal/custom-modal.component';
import {PaymentBlockModalComponent, PaymentBlockOutcome} from './components/payment-block-modal/payment-block-modal.component';
import {LocalStorageUtils} from './shared/utils/local-storage-utils';
import {environment} from '../environments/environment';
import {SessionEventService} from './shared/services/session-event-service';
import {AdditionalPACData} from './models/additional-pac-data.model';
import {PaymentErrorService} from './shared/services/payment-error-service';
import {AppConstants} from './shared/app-constants';

const PAYMENT_BLOCK_POLL_INTERVAL_MS = 5000;
// The block is a Redis key with a 5-minute TTL, so it auto-clears at expiry regardless of the
// actual payment outcome - a poll landing in this final window can't be trusted to mean
// "the payment succeeded", it may just be catching Redis's own natural expiry. Stop polling
// this many seconds before the countdown reaches 0 so no such response is in flight there.
const PAYMENT_BLOCK_POLL_CUTOFF_SECONDS = 4;
const PAYMENT_SUCCESS_MESSAGE_CODE = '100';
const PAYMENT_FAILURE_MESSAGE_CODE = '500';
// Cleared as soon as the block popup shows, not just on resolve: a hard/manual reload of a page
// that still carries these from the CyberSource redirect would otherwise re-parse
// messageCode=payment_processing and re-trigger the popup purely from stale URL state, even
// though the live block check (GET /additional-pac-info, which every page already calls) is the
// actual source of truth and will correctly re-show the popup on its own if still blocked.
const REDIRECT_PARAMS_TO_CLEAR = ['messageCode', 'retryAfterSeconds', 'status', 'reasonCode', 'transactionId', 'amount'];

@Component({
  selector: 'app-root',
  standalone: true,
  imports: [CommonModule, RouterOutlet, HeaderComponent, FooterComponent, CustomModalComponent, PaymentBlockModalComponent],
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss']
})
export class AppComponent implements OnInit {
  title = 'ifas-payment-portal-fe';

  public loadingService = inject(LoaderService);
  private readonly idleTimeoutService = inject(IdleTimeoutService); //mark it as readonly
  private readonly paymentBlockService = inject(PaymentBlockService);
  private readonly pacService = inject(PacService);
  private readonly paymentErrorService = inject(PaymentErrorService);
  private readonly sessionEventService = inject(SessionEventService);
  private readonly router = inject(Router);
  private countdownInterval: any;
  private paymentBlockCountdownInterval: any;
  private paymentBlockPollInterval: any;
  private paymentBlockResolveTimeout: any;
  private paymentBlockResolved = false;
  private paymentBlockPacNumber = '';


  isTerminal: boolean = false;

  idleMessage = '';
  paymentBlockRemainingSeconds = 0;
  paymentBlockOutcome: PaymentBlockOutcome = 'processing';
  paymentBlockOutcomeMessage = '';

  @ViewChild('idleTimeoutModalRef') idleTimeoutModalRef!: CustomModalComponent;
  @ViewChild('paymentBlockModalRef') paymentBlockModalRef!: PaymentBlockModalComponent;

  ngOnInit(): void {
    const script = document.createElement('script');
    script.src = `https://www.google.com/recaptcha/api.js?render=${environment.recaptcha.siteKey}`;
    document.head.appendChild(script);
    this.isTerminal = LocalStorageUtils.isOnTerminal();

    document.addEventListener('wheel', function (event) {
      const activeElement = document.activeElement as HTMLElement;
      // Prefer using an optional chain expression instead, as it's more concise and easier to read
      if (
        activeElement?.tagName === 'INPUT' &&
        (activeElement as HTMLInputElement).type === 'number'
      ) {
        (activeElement as HTMLInputElement).blur();
      }
    }, { passive: true });
    this.idleTimeoutService.startWatching();

    this.idleTimeoutService.showSimpleTimeoutModal$.subscribe(({ idle, warning }) => {
      let countdown = warning * 60; // 1 minute * 60 = 60 seconds

      this.updateIdleMessage(countdown);
      this.idleTimeoutModalRef?.showModal();

      // clear any previous interval
      if (this.countdownInterval) {
        clearInterval(this.countdownInterval);
      }

      // start ticking down every second
      this.countdownInterval = setInterval(() => {
        countdown--;
        if (countdown >= 0) {
          this.updateIdleMessage(countdown);
        }
        if (countdown <= 0) {
          clearInterval(this.countdownInterval);
        }
      }, 1000);
    });

    this.idleTimeoutService.hideTimeoutModal$.subscribe(() => {
      this.idleTimeoutModalRef?.hideModal();
      if (this.countdownInterval) {
        clearInterval(this.countdownInterval);
      }
    });

    this.paymentBlockService.showPaymentBlockedModal$.subscribe(({ pacNumber, retryAfterSeconds }) => {
      let countdown = retryAfterSeconds;
      this.paymentBlockPacNumber = pacNumber;
      this.paymentBlockResolved = false;
      this.paymentBlockOutcome = 'processing';
      this.paymentBlockOutcomeMessage = '';
      if (this.paymentBlockResolveTimeout) {
        clearTimeout(this.paymentBlockResolveTimeout);
      }

      this.clearRedirectQueryParams();
      this.paymentBlockRemainingSeconds = countdown;
      this.paymentBlockModalRef?.showModal();

      if (this.paymentBlockCountdownInterval) {
        clearInterval(this.paymentBlockCountdownInterval);
      }
      if (this.paymentBlockPollInterval) {
        clearInterval(this.paymentBlockPollInterval);
      }

      this.paymentBlockCountdownInterval = setInterval(() => {
        countdown--;
        if (countdown > 0) {
          this.paymentBlockRemainingSeconds = countdown;
          if (countdown <= PAYMENT_BLOCK_POLL_CUTOFF_SECONDS && this.paymentBlockPollInterval) {
            clearInterval(this.paymentBlockPollInterval);
          }
        } else {
          // The TTL has now expired in Redis regardless of outcome, so there's nothing left to
          // confirm - a check here would just see the same natural expiry every time and always
          // read as "success". The countdown reaching 0 without an earlier poll-detected clear
          // is itself the failure signal.
          this.resolvePaymentBlock(pacNumber);
        }
      }, 1000);

      // Poll independently of the countdown so an early clear (e.g. the CyberSource email
      // confirmation lands before the 5-minute TTL expires) refreshes in place immediately
      // instead of making the user wait out the full window. Uses SKIP_LOADER (see PacService)
      // so this background call never flashes the full-screen loader.
      this.paymentBlockPollInterval = setInterval(() => {
        this.pacService.checkBlockStatus(pacNumber).subscribe({
          next: (data) => {
            if (!data?.blocked) {
              this.resolvePaymentBlock(pacNumber, data);
            }
          },
          error: () => {
            // transient network hiccup - keep polling on the next tick
          }
        });
      }, PAYMENT_BLOCK_POLL_INTERVAL_MS);
    });
  }

  /**
   * Called when the payment block clears - either polling detects it was cleared early (within
   * the 5-minute window, meaning the payment actually went through - treated as success), or the
   * countdown reaches 0 with the block never having cleared early (meaning the payment never
   * completed in time - treated as failure). Refreshes the affected page's PAC data in place
   * (via PaymentBlockService.blockResolved$, applied by whichever of court-payment /
   * court-deposit-details is currently active), shows the matching result on the popup for
   * AppConstants.MESSAGE_CLEAR_DELAY_MS (same 15s convention used elsewhere for message
   * auto-clear), then closes it - no hard reload, so there's no page flash and no risk of
   * re-parsing a stale redirect query param.
   */
  private resolvePaymentBlock(pacNumber: string, freshData?: AdditionalPACData): void {
    if (this.paymentBlockResolved) {
      return;
    }
    this.paymentBlockResolved = true;
    clearInterval(this.paymentBlockCountdownInterval);
    clearInterval(this.paymentBlockPollInterval);
    this.paymentBlockRemainingSeconds = 0;

    this.paymentBlockOutcome = freshData ? 'success' : 'failure';
    const messageCode = freshData ? PAYMENT_SUCCESS_MESSAGE_CODE : PAYMENT_FAILURE_MESSAGE_CODE;
    this.paymentErrorService.getMessage(messageCode, this.paymentBlockOutcome).subscribe(message => {
      this.paymentBlockOutcomeMessage = message;
    });

    if (freshData) {
      this.paymentBlockService.notifyResolved(freshData);
    } else {
      this.pacService.checkBlockStatus(pacNumber).subscribe({
        next: (data) => this.paymentBlockService.notifyResolved(data),
        error: () => {
          // popup will still close on the timeout below; the next explicit user action picks up
          // fresh data via the normal fetchAdditionalInfo path regardless
        }
      });
    }

    this.paymentBlockResolveTimeout = setTimeout(() => {
      this.paymentBlockModalRef?.hideModal();
    }, AppConstants.MESSAGE_CLEAR_DELAY_MS);
  }

  /**
   * The popup never traps the user (this may be a shared kiosk/terminal) - Go to Home stops
   * tracking this PAC's block and leaves. If it's still genuinely blocked, the next payment
   * attempt on it will simply surface the popup again via the normal server-side check.
   */
  onPaymentBlockGoHome(): void {
    this.paymentBlockResolved = true;
    clearInterval(this.paymentBlockCountdownInterval);
    clearInterval(this.paymentBlockPollInterval);
    if (this.paymentBlockResolveTimeout) {
      clearTimeout(this.paymentBlockResolveTimeout);
    }
    // Court-deposit PAC: tell the backend the session was abandoned so it can settle the deposit
    // for this PAC; navigate home whether or not that call succeeds
    const pacNumber = this.paymentBlockPacNumber;
    if (pacNumber?.startsWith(AppConstants.DEPOSIT_PAC_PREFIX)) {
      this.pacService.sendSessionStatus(pacNumber, 'ABORTED').subscribe({
        next: () => this.router.navigate(['']),
        error: () => this.router.navigate([''])
      });
      return;
    }
    this.router.navigate(['']);
  }

  /**
   * "Okay" (or the header close button) on a resolved success/failure result - the popup has
   * already hidden itself, so this just cancels the pending auto-close timeout. No navigation:
   * the user stays on the current page (e.g. to download a receipt on success, or to note the
   * error contact details on failure).
   */
  onPaymentBlockAcknowledge(): void {
    if (this.paymentBlockResolveTimeout) {
      clearTimeout(this.paymentBlockResolveTimeout);
    }
  }

  private clearRedirectQueryParams(): void {
    const url = new URL(window.location.href);
    let changed = false;
    REDIRECT_PARAMS_TO_CLEAR.forEach(param => {
      if (url.searchParams.has(param)) {
        url.searchParams.delete(param);
        changed = true;
      }
    });
    if (changed) {
      window.history.replaceState(window.history.state, '', url.toString());
    }
  }


  private updateIdleMessage(countdown: number): void {
    this.idleMessage = `Are you still there?<br> If not, we'll close this session in ${countdown} seconds to main menu.`;
  }

  handleModalClose(confirmed: boolean): void { // NOSONAR
    if (confirmed) { // NOSONAR
      this.onIdleModalOk();
    } else {
      this.onIdleModalCancel();
    }
  }

  onIdleModalOk(): void {
    this.idleTimeoutService.onModalOk();
  }

  onIdleModalCancel(): void {
    // Only court-deposit-details consumes session events; setting it elsewhere would leave a stale
    // EXPIRED in the BehaviorSubject to be replayed against the next deposit PAC
    if (this.router.url.includes('court-deposit-details')) {
      this.sessionEventService.setSessionEvent('EXPIRED');
    }
    this.router.navigate(['']);
  }

}
